// Pure, browser-local text-analysis helpers: LCS line diff, readability metrics,
// bionic-reading formatting, and small text utilities. No external dependencies.

// ---------------------------------------------------------------------------
// Diff (LCS-based line + word diff)
// ---------------------------------------------------------------------------

export type DiffOp = 'equal' | 'add' | 'remove';

export type LineDiffRow = {
  op: DiffOp;
  text: string;
  oldNumber: number | null;
  newNumber: number | null;
  // Word-level segments (only populated for changed pairs that are similar).
  segments?: WordSegment[];
};

export type WordSegment = {
  op: DiffOp;
  text: string;
};

export type DiffResult = {
  rows: LineDiffRow[];
  added: number;
  removed: number;
  unchanged: number;
};

function splitLines(value: string): string[] {
  if (value === '') return [];
  return value.replace(/\r\n?/g, '\n').split('\n');
}

// Longest Common Subsequence table over two token arrays.
function lcsSequence<T>(a: T[], b: T[], equals: (x: T, y: T) => boolean): DiffToken<T>[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = equals(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffToken<T>[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (equals(a[i], b[j])) {
      out.push({ op: 'equal', value: a[i] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: 'remove', value: a[i] });
      i += 1;
    } else {
      out.push({ op: 'add', value: b[j] });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ op: 'remove', value: a[i] });
    i += 1;
  }
  while (j < m) {
    out.push({ op: 'add', value: b[j] });
    j += 1;
  }
  return out;
}

type DiffToken<T> = { op: DiffOp; value: T };

function tokenizeWords(line: string): string[] {
  // Keep whitespace runs as their own tokens so we can rejoin faithfully.
  return line.match(/\s+|\S+/g) ?? [];
}

function similarityRatio(a: string, b: string): number {
  if (!a && !b) return 1;
  const at = tokenizeWords(a).filter((t) => t.trim() !== '');
  const bt = tokenizeWords(b).filter((t) => t.trim() !== '');
  if (at.length === 0 && bt.length === 0) return 1;
  const seq = lcsSequence(at, bt, (x, y) => x === y);
  const common = seq.filter((t) => t.op === 'equal').length;
  const total = at.length + bt.length;
  return total === 0 ? 1 : (2 * common) / total;
}

function wordDiff(oldLine: string, newLine: string): WordSegment[] {
  const seq = lcsSequence(tokenizeWords(oldLine), tokenizeWords(newLine), (x, y) => x === y);
  const segments: WordSegment[] = [];
  for (const token of seq) {
    const last = segments[segments.length - 1];
    if (last && last.op === token.op) {
      last.text += token.value;
    } else {
      segments.push({ op: token.op, text: token.value });
    }
  }
  return segments;
}

export function computeLineDiff(original: string, changed: string): DiffResult {
  const aLines = splitLines(original);
  const bLines = splitLines(changed);
  const seq = lcsSequence(aLines, bLines, (x, y) => x === y);

  const rows: LineDiffRow[] = [];
  let oldNo = 0;
  let newNo = 0;
  let added = 0;
  let removed = 0;
  let unchanged = 0;

  for (let idx = 0; idx < seq.length; idx += 1) {
    const token = seq[idx];
    if (token.op === 'equal') {
      oldNo += 1;
      newNo += 1;
      unchanged += 1;
      rows.push({ op: 'equal', text: token.value, oldNumber: oldNo, newNumber: newNo });
      continue;
    }

    // Detect a remove immediately followed by an add -> treat as a modified pair
    // and compute word-level segments when the lines are reasonably similar.
    const next = seq[idx + 1];
    if (token.op === 'remove' && next && next.op === 'add') {
      const ratio = similarityRatio(token.value, next.value);
      if (ratio >= 0.4) {
        oldNo += 1;
        newNo += 1;
        removed += 1;
        added += 1;
        const segments = wordDiff(token.value, next.value);
        rows.push({
          op: 'remove',
          text: token.value,
          oldNumber: oldNo,
          newNumber: null,
          segments: segments.filter((s) => s.op !== 'add'),
        });
        rows.push({
          op: 'add',
          text: next.value,
          oldNumber: null,
          newNumber: newNo,
          segments: segments.filter((s) => s.op !== 'remove'),
        });
        idx += 1;
        continue;
      }
    }

    if (token.op === 'remove') {
      oldNo += 1;
      removed += 1;
      rows.push({ op: 'remove', text: token.value, oldNumber: oldNo, newNumber: null });
    } else {
      newNo += 1;
      added += 1;
      rows.push({ op: 'add', text: token.value, oldNumber: null, newNumber: newNo });
    }
  }

  return { rows, added, removed, unchanged };
}

// ---------------------------------------------------------------------------
// Readability metrics
// ---------------------------------------------------------------------------

export type ReadabilityMetrics = {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  sentences: number;
  syllables: number;
  avgWordsPerSentence: number;
  avgSyllablesPerWord: number;
  fleschReadingEase: number;
  fleschKincaidGrade: number;
  readingTimeMinutes: number;
  readingTimeLabel: string;
  interpretation: string;
  gradeLabel: string;
};

export function countSyllables(word: string): number {
  const clean = word.toLowerCase().replace(/[^a-z]/g, '');
  if (clean.length === 0) return 0;
  if (clean.length <= 3) return 1;

  const working = clean.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '');

  const groups = working.match(/[aeiouy]{1,2}/g);
  let count = groups ? groups.length : 0;

  // Words that end in a syllabic consonant cluster like "table", "rhythm".
  if (count === 0) count = 1;
  return count;
}

function countTextSyllables(words: string[]): number {
  let total = 0;
  for (const word of words) {
    total += countSyllables(word);
  }
  return total;
}

export function analyzeReadability(text: string): ReadabilityMetrics {
  const trimmed = text.trim();
  const characters = text.length;
  const charactersNoSpaces = text.replace(/\s/g, '').length;

  const words = trimmed.length === 0 ? [] : trimmed.split(/\s+/).filter(Boolean);
  const wordCount = words.length;

  const sentenceMatches = trimmed.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g);
  const sentenceCount = trimmed.length === 0 ? 0 : Math.max(sentenceMatches?.length ?? 0, 1);

  const syllables = countTextSyllables(words);

  const avgWordsPerSentence = sentenceCount > 0 ? wordCount / sentenceCount : 0;
  const avgSyllablesPerWord = wordCount > 0 ? syllables / wordCount : 0;

  const fleschReadingEase =
    wordCount === 0 ? 0 : 206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord;
  const fleschKincaidGrade =
    wordCount === 0 ? 0 : 0.39 * avgWordsPerSentence + 11.8 * avgSyllablesPerWord - 15.59;

  const readingTimeMinutes = wordCount / 200; // ~200 wpm silent reading
  const readingTimeLabel = formatReadingTime(readingTimeMinutes);

  return {
    characters,
    charactersNoSpaces,
    words: wordCount,
    sentences: sentenceCount,
    syllables,
    avgWordsPerSentence: round(avgWordsPerSentence, 1),
    avgSyllablesPerWord: round(avgSyllablesPerWord, 2),
    fleschReadingEase: round(clampEase(fleschReadingEase), 1),
    fleschKincaidGrade: round(Math.max(fleschKincaidGrade, 0), 1),
    readingTimeMinutes: round(readingTimeMinutes, 2),
    readingTimeLabel,
    interpretation: interpretEase(fleschReadingEase),
    gradeLabel: gradeLabel(fleschKincaidGrade),
  };
}

function clampEase(value: number): number {
  if (value > 121) return 121;
  if (value < 0) return 0;
  return value;
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function formatReadingTime(minutes: number): string {
  if (minutes <= 0) return '0 sec';
  if (minutes < 1) {
    const seconds = Math.max(1, Math.round(minutes * 60));
    return `${seconds} sec`;
  }
  const whole = Math.floor(minutes);
  const seconds = Math.round((minutes - whole) * 60);
  if (seconds === 0) return `${whole} min`;
  return `${whole} min ${seconds} sec`;
}

function interpretEase(score: number): string {
  if (score >= 90) return 'Very easy to read. Understood by an average 11-year-old.';
  if (score >= 80) return 'Easy to read. Conversational English for consumers.';
  if (score >= 70) return 'Fairly easy to read. Suitable for a wide audience.';
  if (score >= 60) return 'Plain English. Easily understood by 13-15 year olds.';
  if (score >= 50) return 'Fairly difficult to read. Some effort required.';
  if (score >= 30) return 'Difficult to read. Best suited to college-level readers.';
  return 'Very difficult to read. Best understood by university graduates.';
}

function gradeLabel(grade: number): string {
  const g = Math.max(grade, 0);
  if (g < 1) return 'Kindergarten level';
  if (g <= 5) return `Grade ${Math.round(g)} (elementary)`;
  if (g <= 8) return `Grade ${Math.round(g)} (middle school)`;
  if (g <= 12) return `Grade ${Math.round(g)} (high school)`;
  return 'College level';
}

// ---------------------------------------------------------------------------
// Bionic reading
// ---------------------------------------------------------------------------

// fixation: 1 (light) .. 5 (strong) -> larger bold fraction.
function boldFractionFor(fixation: number): number {
  const map: Record<number, number> = {
    1: 0.35,
    2: 0.45,
    3: 0.55,
    4: 0.65,
    5: 0.75,
  };
  return map[fixation] ?? 0.5;
}

function boldLenForWord(coreLength: number, fixation: number): number {
  if (coreLength <= 0) return 0;
  if (coreLength <= 1) return 1;
  if (coreLength <= 3) return Math.max(1, Math.round(coreLength * 0.4));
  const fraction = boldFractionFor(fixation);
  return Math.max(1, Math.min(coreLength - 1, Math.ceil(coreLength * fraction)));
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Convert one token (a word, possibly with leading/trailing punctuation) into
// bionic HTML by bolding the leading letters of its alphabetic core.
function bionicToken(token: string, fixation: number): string {
  const match = token.match(/^(\W*)([\p{L}\p{N}]+)([\s\S]*)$/u);
  if (!match) return escapeHtml(token);
  const [, lead, core, rest] = match;
  const boldLen = boldLenForWord(core.length, fixation);
  const boldPart = core.slice(0, boldLen);
  const restPart = core.slice(boldLen);
  return (
    escapeHtml(lead) + `<b>${escapeHtml(boldPart)}</b>` + escapeHtml(restPart) + escapeHtml(rest)
  );
}

export function bionicToHtml(text: string, fixation: number): string {
  if (text.trim() === '') return '';
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  return lines
    .map((line) => {
      const tokens = line.match(/\s+|\S+/g) ?? [];
      return tokens.map((tok) => (/^\s+$/.test(tok) ? tok : bionicToken(tok, fixation))).join('');
    })
    .join('<br />\n');
}

export const SAMPLE_READABILITY_TEXT =
  'Good writing respects the reader. Short sentences carry a single idea. When you keep words simple and paragraphs tight, people finish what they start. Clear text is not dumbed down; it is considerate. Measure it, then trim what slows the reader down.';

export const SAMPLE_DIFF_ORIGINAL =
  'The quick brown fox\njumps over the lazy dog.\nPack my box with five dozen jugs.\nA final shared line.';

export const SAMPLE_DIFF_CHANGED =
  'The quick red fox\njumps over the lazy dog.\nPack my crate with five dozen jugs.\nAn extra new line here.\nA final shared line.';

export const SAMPLE_BIONIC_TEXT =
  'Bionic reading guides your eyes by fixating on the leading letters of each word so your brain can complete the rest. Paste any passage to see the effect instantly.';
